<template>
  <div data-cy="vendor-assessment-heading">
    <div
      v-if="assessmentQuestion.checklistItemIsHeading"
      :id="`vendor-assessment-question-detail-heading-${assessmentQuestion.itemMasterId}`"
      :data-cy="`assessment-question-attachment-${idxHeading}`"
      :class="`tw-cursor-pointer tw-select-none tw-pr-[25px] tw-bg-[#FCFCFD] tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center tw-self-stretch tw-border-0 tw-border-b-[1px] tw-border-solid tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)] tw-border-[#EFF0F1]`"
      @click="collapseCategory"
    >
      <div
        class="tw-flex tw-flex-row tw-justify-start tw-items-center tw-py-[20px]"
      >
        <CustomCheckbox
          v-model:is-checked="selectAll"
          class="tw-h-[20px] tw-ml-[25px] !tw-mb-0"
          size="20px"
          @update:is-checked="handleSelfCheck"
        />
        <div class="tw-flex tw-items-center tw-gap-2">
          <div
            class="tw-font-Poppins tw-text-[18px] tw-font-[600] tw-text-[#211F58]"
            data-cy="vendor-assessment-question-name"
          >
            {{
              assessmentQuestion.checklistItemHeadingLabel ||
              assessmentQuestion.checklistItemName ||
              ""
            }}
          </div>
        </div>
      </div>
      <div class="tw-flex tw-items-center tw-gap-[15px]">
        <div class="tw-text-[#B7B7B7] tw-text-[11px] tw-leading-[130%]">
          <div v-if="!assessmentQuestion?.checklistItemChildren">
            <span>{{
              getComponentUiText("app-component-assessments-no-question-1")
            }}</span>
          </div>
          <div v-else>
            <span>{{
              `${assessmentQuestion.checklistItemChildren?.length} `
            }}</span>
            <span>{{
              assessmentQuestion.checklistItemChildren?.length > 1
                ? getComponentUiText(
                    "app-component-assessments-multiple-question-counter-1",
                  )
                : getComponentUiText(
                    "app-component-assessments-single-question-counter-1",
                  )
            }}</span>
          </div>
        </div>
        <span class="float-right button-arrow dropdown-button-arrow-img">
          <img
            v-if="showIcon"
            class="dropdown-button-arrow-img"
            src="@/assets/img/arrow_down2.svg"
          />
          <img
            v-else
            class="collap-button-arrow-down-img"
            src="@/assets/img/arrow_down2.svg"
          />
        </span>
      </div>
    </div>
    <div v-show="!showIcon">
      <div
        v-for="(item, idx) in assessmentQuestion.checklistItemChildren"
        :id="`vendor-assessment-question-detail-item-${item.checklistItemId}`"
        :key="item.checklistItemDateUpdatedEmailGrant"
      >
        <AssessmentQuestionAttachmentsRequired
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.ATTACH"
          :is-show-required-tag="false"
          :idx="idxHeading + '-' + (idx + 1)"
          :checklist-item="item"
          :selected-questions="selectedQuestions"
          :parent="assessmentQuestion"
          :is-selected-all="selectAll"
          @update:selected-questions="
            emits('update:selected-questions', $event)
          "
          @update:answer="handleUpdateChecklist"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
          @open-reset="showModalResetAnswers(item)"
        />
        <AssessmentQuestionYesNo
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.YES_NO"
          :checklist-item="item"
          :idx="idxHeading + '-' + (idx + 1)"
          :is-show-required-tag="false"
          :is-selected-all="selectAll"
          :selected-questions="selectedQuestions"
          :is-vendor-request="CHECKLIST_TYPE.externalChecklist"
          :compliance-type-value="
            complianceTypeValues.find(
              (i) => i.id === item.checklistItemComplianceTypeId,
            )
          "
          @update:selected="(value) => (selectAll = value)"
          @update:selected-questions="
            emits('update:selected-questions', $event)
          "
          @update:answer="handleUpdateChecklist"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
          @open-reset="showModalResetAnswers(item)"
        />
        <AssessmentQuestionTextArea
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.TEXT"
          :checklist-item="item"
          :is-vendor-request="CHECKLIST_TYPE.externalChecklist"
          :is-show-required-tag="false"
          :is-selected-all="selectAll"
          :idx="idxHeading + '-' + (idx + 1)"
          :placeholder="
            getComponentUiText(
              'app-assessments-vendor-questions-placeholder-add-detail-1',
            )
          "
          :selected-questions="selectedQuestions"
          @update:selected-questions="
            emits('update:selected-questions', $event)
          "
          @update:answer="handleUpdateChecklist"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
          @open-reset="showModalResetAnswers(item)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, Ref, ref, watch } from "vue";
import { getComponentUiText } from "@/utils/utilities";
import CustomCheckbox from "../common/customCheckBox.vue";
import AssessmentQuestionAttachmentsRequired from "../assessments/assessmentQuestionAttachmentsRequired.vue";
import AssessmentQuestionYesNo from "../assessments/assessmentQuestionYesNo.vue";
import AssessmentQuestionTextArea from "../assessments/assessmentQuestionTextArea.vue";
import {
  COMPLIANCE_TYPE,
  RIGHT_SLIDE_MODAL_NAME,
  MODAL_NAME,
  successStatusCodes,
  CHECKLIST_TYPE,
} from "@/utils/constants";
import store from "@/store";
import { IChecklistRequestedComplianceTypeValue } from "@/interfaces/vendor";
import { IModalMessage } from "@/interfaces/common/common";
import {
  deleteChecklistRequestedAttachment,
  IParamsUpdateChecklistRequestedItems,
  updateChecklistRequestedExternalItem,
} from "@/services/vendor";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { showErrorAPI } from "@/services/apiRoutes";
import { Vendor } from "@/services/apiRoutes/routes";

const props = defineProps({
  /** The order number of sub question category */
  orderNumber: {
    type: Number,
    default: 1,
  },
  /** sub sub categories */
  isExclude: {
    type: Boolean,
    default: false,
  },
  assessmentQuestion: {
    type: Object,
    default: () => ({}),
    required: true,
  },
  selectedQuestions: {
    type: Array as () => number[],
    default: () => [],
  },
  complianceTypeValues: {
    type: Array as () => IChecklistRequestedComplianceTypeValue[],
    default: () => [],
  },
  dataVendor: {
    type: Object,
    default: () => ({}),
  },
  idxHeading: {
    type: Number,
    default: 0,
  },
});

const emits = defineEmits(["update:selected-questions"]);

const selectAll: Ref<boolean> = computed(() => {
  if (props.assessmentQuestion.checklistItemChildren?.length) {
    const isSelect = props.assessmentQuestion.checklistItemChildren?.every(
      (item) => props.selectedQuestions.includes(item.itemMasterId),
    );

    const isExitsSeleted = props.selectedQuestions.includes(
      props.assessmentQuestion?.itemMasterId,
    );
    if (!isExitsSeleted && isSelect) {
      handleSelfCheck();
    }

    return isSelect;
  } else {
    return props.selectedQuestions.includes(
      props.assessmentQuestion.itemMasterId,
    );
  }
});

const showIcon: Ref<boolean> = ref(true);

const collapseCategory = () => (showIcon.value = !showIcon.value);

const handleSelfCheck = () => {
  emits("update:selected-questions", {
    item: props.assessmentQuestion,
    selected: selectAll.value,
    level: 0,
  });
};

const handleUpdateChecklist = (value: any) => {
  store.dispatch("Vendor/updateVendorRequestedExternalInput", value);
};

const showAdvanceModal = (item: any, parent, passkey) => {
  const checklistRequestedId =
    store.state.Vendor.checklistVendorRequestedExternalInput
      .checklistRequestedId;

  const emitData: IModalMessage = {
    component: RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_REQUESTED_ADVANCED,
    properties: {
      checklistId: checklistRequestedId,
      checklistItemId: item.checklistItemId,
      questionData: {
        ...item,
        checklistItemCategoryLabel:
          parent.checklistItemHeadingLabel || parent.checklistItemName || "",
      },
      checklistType: CHECKLIST_TYPE.externalChecklist,
      passkey,
    },
    events: {
      "update:selected-questions": (data) => {
        emits("update:selected-questions", data);
      },
    },
  };
  eventEmitter.emit(EmitterEvents.OPEN_RIGHT_SLIDE_MODAL, emitData);
};

const handleUpdateItems = async (item: any) => {
  const checklistRequestedId: number = props.dataVendor.checklistRequestedId;

  try {
    if (item.checklistItemComplianceTypeId !== COMPLIANCE_TYPE.ATTACH) {
      let updatCheckListParams: IParamsUpdateChecklistRequestedItems = {
        checklistRequestedItems: [],
      };
      if (item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.YES_NO) {
        updatCheckListParams = {
          checklistRequestedItems: [
            {
              checklistRequestedItemId: item.checklistItemId,
              complianceTypeValueId: null,
            },
          ],
        };
      }
      if (item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.TEXT) {
        updatCheckListParams = {
          checklistRequestedItems: [
            {
              checklistRequestedItemId: item.checklistItemId,
              compensatingControlDescription: "",
            },
          ],
        };
      }
      const res = await updateChecklistRequestedExternalItem(
        checklistRequestedId,
        updatCheckListParams,
      );
      if (res.status === successStatusCodes.OK) {
        await store.dispatch("Vendor/fetchVendorRequestedExternalInput");
        // update this question
        eventEmitter.emit(EmitterEvents.CLOSE_MODAL);
      }
    } else {
      const attachmentIds = item.checklistItemAttachments.map(
        (attachment: any) => attachment.attachmentId,
      );

      if (attachmentIds.length) {
        const res = await deleteChecklistRequestedAttachment({
          checklistItemId: item.checklistItemId,
          checklistRequestedId: checklistRequestedId,
          checklistItemAttachmentIds: attachmentIds,
        });
        if (res.status === successStatusCodes.OK) {
          await store.dispatch("Vendor/fetchVendorRequestedExternalInput");
          // update this question
          eventEmitter.emit(EmitterEvents.CLOSE_MODAL);
        }
      }
    }
  } catch (error) {
    showErrorAPI(error, Vendor.putChecklistRequestedExternalItem);
  }
};

const showModalResetAnswers = (item: any) => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText(
        "app-component-assessments-question-bulk-action-item-reset-answer-1",
      ),
      description: getComponentUiText(
        "app-assessment-modal-reset-answer-description-1",
      ),
      hiddenHelp: false,
      buttonText: getComponentUiText(
        "app-assessment-modal-reset-answers-button-text-1",
      ),
      buttonRed: true,
      class: "tw-top-[0px]",
    },
    events: {
      "update:data": () => {
        handleUpdateItems(item);
      },
    },
  };
  eventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};
</script>

<style lang="scss" scoped>
.box-shadow-contain {
  box-shadow: 2px 0px 20px 0px rgba(33, 31, 88, 0.1);
}

.search-box {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  padding: 11px 15px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  max-width: 450px;
  width: 100%;
  outline: none;
  background: white;
  margin-right: 15px;
}

.search-box input {
  border: none;
  outline: none;
  width: 100%;
  margin-left: 10px;
}
.toggle-switch-view {
  border-radius: 6px;
  background: var(--ui-white, #fff);
  backdrop-filter: blur(6px);
  // padding: 4px;
  align-items: center;
  display: inline-flex;

  label {
    margin-bottom: 0;
  }
  .label {
    display: flex;
    cursor: pointer;
    padding: 10px 18px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0;
    color: var(--ui-light-grey, #b7b7b7);
    letter-spacing: 1px;
    border-radius: 6px;
    min-width: 16px;
    // Keep this coded out for now

    // min-width: 111px;
    // background: var(--brand-navy, #211F58);
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;

    &:checked + .label {
      background: var(--brand-navy, #211f58);
      color: #fff;
    }
  }
  &.setting-option-selected {
    background: var(--brand-navy, #211f58);
  }
}
.collap-button-arrow-down-img {
  transform: rotate(180deg);
}
</style>
